import React from 'react';
import {
  IonButton,
  IonButtons,
  IonInput,
  IonIcon,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonToolbar,
  IonTitle,
  IonText,
  IonRow,
  IonCol,
  IonCard,
  IonCardHeader,
  IonGrid
} from '@ionic/react';
import { arrowBack } from 'ionicons/icons';

// Props interfaces
export interface AuthInputProps {
  label: string;
  type: 'text' | 'email' | 'password';
  placeholder: string;
  value: string;
  onInput: (value: string) => void;
  icon?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

export interface AuthButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'secondary' | 'outline';
  expand?: 'block' | 'full';
  className?: string;
}

export interface AuthHeaderProps {
  title: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export interface AuthCheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export interface AuthDividerProps {
  text?: string;
}

// Reusable Auth Input Component
export const AuthInput: React.FC<AuthInputProps> = ({
  label,
  type,
  placeholder,
  value,
  onInput,
  icon,
  disabled = false,
  required = false,
  className = ''
}) => (
  <IonInput
    className={`${className} ion-margin-top`}
    labelPlacement="stacked"
    label={label}
    type={type}
    placeholder={placeholder}
    value={value}
    onIonInput={(e) => onInput(e.detail.value!)}
    disabled={disabled}
    required={required}
  >
    {icon && <IonIcon icon={icon} slot="start" />}
  </IonInput>
);

// Reusable Auth Button Component
export const AuthButton: React.FC<AuthButtonProps> = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  variant = 'primary',
  expand = 'block',
  className = ''
}) => {
  const getFill = () => {
    switch (variant) {
      case 'outline': return 'outline';
      case 'secondary': return 'clear';
      default: return 'solid';
    }
  };

  return (
    <IonButton
      expand={expand}
      fill={getFill()}
      onClick={onClick}
      disabled={disabled || loading}
      className={className}
    >
      {children}
    </IonButton>
  );
};

// Reusable Auth Header Component
export const AuthHeader: React.FC<AuthHeaderProps> = ({
  title,
  showBackButton = false,
  onBackClick,
  disabled = false,
  className = ''
}) => (
  <IonToolbar className={className}>
    {showBackButton && (
      <IonButtons slot="start">
        <IonButton
          fill="clear"
          onClick={onBackClick}
          disabled={disabled}
        >
          <IonIcon icon={arrowBack} />
        </IonButton>
      </IonButtons>
    )}
    <IonTitle>{title}</IonTitle>
  </IonToolbar>
);

// Reusable Card Wrapper Component
export interface AuthCardProps {
  title: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const AuthCard: React.FC<AuthCardProps> = ({
  title,
  showBackButton = false,
  onBackClick,
  disabled = false,
  children,
  className = ''
}) => (
  <IonCard className={className}>
    <IonCardHeader className="cardHeader">
      <AuthHeader 
        title={title}
        showBackButton={showBackButton}
        onBackClick={onBackClick}
        disabled={disabled}
      />
    </IonCardHeader>
    {children}
  </IonCard>
);

// Reusable Auth Checkbox Component
export const AuthCheckbox: React.FC<AuthCheckboxProps> = ({
  label,
  checked,
  onChange,
  disabled = false,
  className = ''
}) => (
  <IonItem lines="none" className={className}>
    <IonCheckbox
      slot="start"
      checked={checked}
      onIonChange={(e) => onChange(e.detail.checked)}
      disabled={disabled}
    />
    <IonLabel slot="start">
      <span>{label}</span>
    </IonLabel>
  </IonItem>
);

// Reusable Divider Component
export const AuthDivider: React.FC<AuthDividerProps> = ({ text = 'or' }) => (
  <IonRow className="ion-justify-content-center">
    <IonCol size="auto">
      <IonText color="medium">
        {text}
      </IonText>
    </IonCol>
  </IonRow>
);

// Reusable Form Wrapper Component
export interface AuthFormProps {
  onSubmit: (e: React.FormEvent) => void;
  children: React.ReactNode;
  className?: string;
}

export const AuthForm: React.FC<AuthFormProps> = ({
  onSubmit,
  children,
  className = ''
}) => (
  <IonGrid className={className}>
    <IonRow>
      <IonCol>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(e); }}>
          {children}
        </form>
      </IonCol>
    </IonRow>
  </IonGrid>
);
